import { useCallback, useEffect, useRef, useState } from "react";
import axiosClient from "../api/axiosClient";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "./admin.css";
import "./ManageBookings.css";

/* ── badge helpers ── */
const statusBadge = (s) => {
  const map = {
    pending:   "badge-yellow",
    confirmed: "badge-blue",
    cancelled: "badge-red",
    completed: "badge-green",
  };
  return map[s] || "badge-gray";
};
const paymentBadge = (s) => (s === "paid" ? "badge-green" : "badge-yellow");

/* ── format date nicely ── */
const fmt = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

/* ── debounce hook ── */
function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/* ============================================================
   MANAGE BOOKINGS
============================================================ */
function ManageBookings() {
  const [bookings,  setBookings]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [page,      setPage]      = useState(1);
  const [pages,     setPages]     = useState(1);
  const [total,     setTotal]     = useState(0);

  /* filter state */
  const [searchRaw, setSearchRaw] = useState("");
  const [status,    setStatus]    = useState("");
  const [payment,   setPayment]   = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate,   setEndDate]   = useState("");

  const search = useDebounce(searchRaw, 450);

  /* count active filters for badge */
  const activeFilters = [search, status, payment, startDate, endDate].filter(Boolean).length;

  const resetFilters = () => {
    setSearchRaw("");
    setStatus("");
    setPayment("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  /* ── fetch ── */
  const loadBookings = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      page,
      limit: 10,
      ...(search    && { search }),
      ...(status    && { status }),
      ...(payment   && { payment }),
      ...(startDate && { startDate }),
      ...(endDate   && { endDate }),
    });
    axiosClient
      .get(`/admin/bookings?${params}`)
      .then((res) => {
        setBookings(res.data.bookings || []);
        setPages(res.data.pages || 1);
        setTotal(res.data.total  || 0);
      })
      .catch((err) => {
        console.error("Failed to load bookings", err);
        toast.error("Failed to load bookings");
      })
      .finally(() => setLoading(false));
  }, [page, search, status, payment, startDate, endDate]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  /* reset page when filters change */
  useEffect(() => {
    setPage(1);
  }, [search, status, payment, startDate, endDate]);

  /* ── pickup / return ── */
  const updatePickup = async (id) => {
    const result = await Swal.fire({
      title: "Confirm Pickup?",
      text: "Mark this booking as Picked Up?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, picked up!",
      confirmButtonColor: "#4f46e5",
    });
    if (!result.isConfirmed) return;
    axiosClient
      .put(`/admin/bookings/pickup/${id}`)
      .then(() => { toast.success("Pickup status updated"); loadBookings(); })
      .catch(() => toast.error("Failed to update pickup status"));
  };

  const updateReturn = async (id) => {
    const result = await Swal.fire({
      title: "Confirm Return?",
      text: "Mark this booking as Returned?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, returned!",
      confirmButtonColor: "#4f46e5",
    });
    if (!result.isConfirmed) return;
    axiosClient
      .put(`/admin/bookings/return/${id}`)
      .then(() => { toast.success("Return status updated"); loadBookings(); })
      .catch(() => toast.error("Failed to update return status"));
  };

  /* ============================================================
     RENDER
  ============================================================ */
  return (
    <>
      {/* ── Header ── */}
      <div className="admin-page-header">
        <h1>📋 Bookings</h1>
        <p>Monitor and manage all rental bookings</p>
      </div>

      {/* ── Filter Bar ── */}
      <div className="mb-filter-card">
        <div className="mb-filter-header">
          <span className="mb-filter-title">
            🔍 Filters
            {activeFilters > 0 && (
              <span className="mb-active-badge">{activeFilters} active</span>
            )}
          </span>
          {activeFilters > 0 && (
            <button className="mb-reset-btn" onClick={resetFilters}>
              ✕ Reset filters
            </button>
          )}
        </div>

        <div className="mb-filter-grid">
          {/* Search */}
          <div className="mb-filter-group">
            <label className="mb-filter-label">Search</label>
            <div className="mb-search-wrap">
              <span className="mb-search-icon">🔎</span>
              <input
                className="mb-filter-input"
                type="text"
                placeholder="User name, email or bike…"
                value={searchRaw}
                onChange={(e) => setSearchRaw(e.target.value)}
              />
              {searchRaw && (
                <button className="mb-clear-btn" onClick={() => setSearchRaw("")}>✕</button>
              )}
            </div>
          </div>

          {/* Booking Status */}
          <div className="mb-filter-group">
            <label className="mb-filter-label">Booking Status</label>
            <select
              className="mb-filter-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="pending">⏳ Pending</option>
              <option value="confirmed">✅ Confirmed</option>
              <option value="cancelled">❌ Cancelled</option>
              <option value="completed">🏁 Completed</option>
            </select>
          </div>

          {/* Payment */}
          <div className="mb-filter-group">
            <label className="mb-filter-label">Payment</label>
            <select
              className="mb-filter-select"
              value={payment}
              onChange={(e) => setPayment(e.target.value)}
            >
              <option value="">All Payments</option>
              <option value="paid">💚 Paid</option>
              <option value="pending">🟡 Pending</option>
            </select>
          </div>

          {/* Date From */}
          <div className="mb-filter-group">
            <label className="mb-filter-label">From Date</label>
            <input
              className="mb-filter-input"
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          {/* Date To */}
          <div className="mb-filter-group">
            <label className="mb-filter-label">To Date</label>
            <input
              className="mb-filter-input"
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ── Results info ── */}
      {!loading && (
        <div className="mb-results-info">
          Showing <strong>{bookings.length}</strong> of <strong>{total}</strong> bookings
        </div>
      )}

      {/* ── Table / States ── */}
      {loading ? (
        <div className="mb-skeleton-wrap">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="mb-skeleton-row" style={{ animationDelay: `${i * 0.07}s` }} />
          ))}
        </div>
      ) : bookings.length === 0 ? (
        <div className="admin-empty">
          <div className="mb-empty-icon">📭</div>
          <h3 className="mb-empty-title">No bookings found</h3>
          <p className="mb-empty-sub">
            {activeFilters > 0
              ? "No bookings match your current filters. Try adjusting or resetting them."
              : "There are no bookings yet."}
          </p>
          {activeFilters > 0 && (
            <button className="btn btn-primary mb-empty-reset" onClick={resetFilters}>
              Reset Filters
            </button>
          )}
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Bike</th>
                <th>Start</th>
                <th>End</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b, idx) => (
                <tr key={b._id}>
                  <td className="mb-row-num">{(page - 1) * 10 + idx + 1}</td>
                  <td>
                    <div className="mb-user-cell">
                      <div className="mb-user-avatar">
                        {(b.userId?.name || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="mb-user-name">{b.userId?.name || "—"}</div>
                        <div className="mb-user-email">{b.userId?.email || ""}</div>
                      </div>
                    </div>
                  </td>
                  <td className="mb-bike-name">{b.bikeId?.name || "—"}</td>
                  <td className="mb-date-cell">{fmt(b.startDate)}</td>
                  <td className="mb-date-cell">{fmt(b.endDate)}</td>
                  <td className="mb-amount">₹{b.totalAmount.toLocaleString("en-IN")}</td>
                  <td>
                    <span className={`badge ${paymentBadge(b.paymentStatus)}`}>
                      {b.paymentStatus}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${statusBadge(b.bookingStatus)}`}>
                      {b.bookingStatus}
                    </span>
                  </td>
                  <td>
                    <div className="mb-actions">
                      {!b.pickupStatus && (
                        <button
                          className="btn btn-success btn-sm"
                          title="Mark as picked up"
                          onClick={() => updatePickup(b._id)}
                        >
                          🛵 Pickup
                        </button>
                      )}
                      {b.pickupStatus && !b.returnStatus && (
                        <button
                          className="btn btn-warning btn-sm"
                          title="Mark as returned"
                          onClick={() => updateReturn(b._id)}
                        >
                          🔁 Return
                        </button>
                      )}
                      {b.returnStatus && (
                        <span className="badge badge-gray">✓ Closed</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {!loading && pages > 1 && (
        <div className="mb-pagination">
          <button
            className="btn btn-ghost btn-sm"
            disabled={page <= 1}
            onClick={() => setPage(1)}
          >
            «
          </button>
          <button
            className="btn btn-ghost btn-sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            ‹ Prev
          </button>

          <div className="mb-page-pills">
            {[...Array(pages)].map((_, i) => {
              const p = i + 1;
              if (pages <= 7 || p === 1 || p === pages || Math.abs(p - page) <= 1) {
                return (
                  <button
                    key={p}
                    className={`mb-page-pill ${p === page ? "active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                );
              }
              if (p === 2 && page > 4) return <span key="e1" className="mb-ellipsis">…</span>;
              if (p === pages - 1 && page < pages - 3) return <span key="e2" className="mb-ellipsis">…</span>;
              return null;
            })}
          </div>

          <button
            className="btn btn-ghost btn-sm"
            disabled={page >= pages}
            onClick={() => setPage(page + 1)}
          >
            Next ›
          </button>
          <button
            className="btn btn-ghost btn-sm"
            disabled={page >= pages}
            onClick={() => setPage(pages)}
          >
            »
          </button>
        </div>
      )}
    </>
  );
}

export default ManageBookings;